import React, { useMemo } from 'react'
import { Stack } from '@mui/material'
import { Bar } from 'recharts'
import { useTranslation } from 'react-i18next'
import { useQuery, gql } from '@apollo/client'
import { useSites } from 'store'
import { TooltipList, Card, Legend } from 'packages/ui/Graph'
import Graph from './BarChart'

import { MONTHS, TOP_5_COLORS } from 'pages/Stats/helpers'
import { useYearRange } from '../Traffic/store'
import { MonthlyPurchasesSkeleton } from './MonthlyPurchasesSkeleton'

export const OPERATION_COUPONS_MONTHLY = gql`
  query CouponsStatsReport($year: Int!, $siteSfIds: [ID]) {
    stats: couponsStatsReport(year: $year, site_sf_ids: $siteSfIds) {
      values {
        key
        value
      }
    }
  }
`

export const OPERATION_SALES_MONTHLY = gql`
  query PurchasesStatsReport($year: Int!, $siteSfIds: [ID]) {
    stats: purchasesStatsReport(year: $year, site_sf_ids: $siteSfIds) {
      values {
        key
        value
      }
    }
  }
`

type CustomTooltipProps = {
  label?: string;
  precision?: string;
  payload?: {name: string, value: string}[];
}

const CustomTooltip = ({ label, precision = '', payload = [] }:CustomTooltipProps) => {
  const { t } = useTranslation()
  return (
    <TooltipList
      title={t(`ui_elements.months.${label}`)}
      list={payload.map((item, index) => ({
        color: TOP_5_COLORS[index],
        label: item.name === 'other' ? t('ui_elements.other') : item.name,
        value: `${item.value.toLocaleString()} ${precision} € HT`,
      }))}
    />
  )
}

const Legends = ({coupons}) => {
  const { t } = useTranslation()
  return (
    <Stack gap={1}>
      <Stack direction={'row'} gap={3} alignSelf={'end'}>
        {
          coupons.slice(0,3).map((coupon, index) => <Legend color={TOP_5_COLORS[index]} label={coupon.title} key={coupon.title} />)
        }
      </Stack>
      {
        coupons.length > 3 && (
          <Stack direction={'row'} gap={3} alignSelf={'end'}>
            {
              coupons.slice(3,5).map((coupon, index) => <Legend color={TOP_5_COLORS[index + 3]} label={coupon.title} key={coupon.title} />)
            }
            {
              coupons.length > 5 && (
                <Legend color={TOP_5_COLORS[5]} label={t('ui_elements.other')} />
              )
            }
          </Stack>
        )
      }
    </Stack>
  )
}

const Coupons = ({title, names, coupons}) => {
  const graph = useMemo(() => {
    return MONTHS.map((month, index) => ({
      month,
      ...coupons.slice(0, 5).reduce((acc, coupon) => {
        acc[coupon.title] = coupon.values[index]
        return acc
      }, {}),
      other: coupons.slice(5).reduce((acc, coupon) => {
        return acc + coupon.values[index]
      }, 0)
    }))
  }, [coupons])

  return (
    <Stack flex={1}>
      <Card
        title={title}
        legend={<Legends coupons={coupons} />}
        sx={{ borderRadius: 4 }}
      >
        <Graph data={graph} tooltip={<CustomTooltip />}>
          {
            names.slice(0, 5).map((key, index) => (
              <Bar dataKey={key} stackId={'a'} fill={TOP_5_COLORS[index]} />
            ))
          }
          <Bar dataKey={'other'} stackId={'a'} fill={TOP_5_COLORS[5]} />
        </Graph>
      </Card>
    </Stack>
  )
}

const PurchasesLoader = ({title, query}) => {
  const [{ selected }] = useSites()
  const [yearRange] = useYearRange()
  const siteSfIds = useMemo(
    () => (selected ? selected.map(({ sf_id }) => sf_id) : []),
    [selected],
  )

  const {data, loading} = useQuery(query, {
    variables: {
      siteSfIds,
      year: yearRange
    }
  })

  const keys = useMemo(() => {
    if (!data?.stats) return []
    return data.stats[0].values
      .map(({key}) => key)
  }, [data])

  const coupons = useMemo(() => {
    return keys.map(key => {
      return {
        title: key,
        values: data.stats.map(item => {
          const value = item.values.find(value => key === value.key)?.value || 0
          return Math.round(value)
        }),
        total: data.stats.reduce((total, item) => {
          const value = item.values.find(value => key === value.key)?.value || 0
          return total + Math.round(value)
        }, 0)
      }
    })
  }, [data, keys])

  return loading ? (
    <MonthlyPurchasesSkeleton />
  ) : (
    <Coupons title={title} coupons={coupons} names={keys} />
  )
}

export default PurchasesLoader
